import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import type { EventFeature, ShopPlaceFeature } from '../api';
import { visited, useVisitedVersion } from '../state/visited';

export interface ViewChange {
  lat: number;
  lng: number;
  z: number;
  bbox: [number, number, number, number];
  userInitiated: boolean;
}

export interface MapViewProps {
  styleUrl: string;
  initial: { lat: number; lng: number; z: number };
  features: EventFeature[];
  /** stop number shown on each exact-location marker, by public_id */
  numbers: Map<string, number>;
  /** the always-visible "browsing layer" of local businesses (shops, farm stands, etc.) */
  shopFeatures: ShopPlaceFeature[];
  /** route-stop number for a shop, by public_id — present only once a built route includes it */
  shopNumbers: Map<string, number>;
  /** where the user is / the route starts, if known */
  origin: { lat: number; lng: number } | null;
  onViewChange: (c: ViewChange) => void;
  onSelect: (publicId: string) => void;
  /** change this object to move the map */
  target: { lat: number; lng: number; z: number; key: number } | null;
  /** called if the map cannot start at all (e.g. no WebGL), so the page can carry on without it */
  onUnavailable?: () => void;
}

const BRAND = '#2f5bea';

/** A pin's color is picked from this palette, stably per listing -- so several sales stacked at
 * nearly the same spot (see the Route 29 corridor: multiple sellers a few hundred feet apart) are
 * still visually distinguishable, without anyone hand-assigning a color to any listing. Every
 * shade here is dark/saturated enough to keep the white pin-number text readable on top of it. */
const PIN_COLORS = ['#2f5bea', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0e7490', '#be185d', '#4f46e5'];

/** Deterministic, not re-rolled on every render/pan -- a pin keeps the same color across the
 * session instead of flickering between colors as the map redraws. */
function pinColor(publicId: string): string {
  let h = 0;
  for (let i = 0; i < publicId.length; i++) h = (h * 31 + publicId.charCodeAt(i)) | 0;
  return PIN_COLORS[Math.abs(h) % PIN_COLORS.length]!;
}
const INK = '#0f172a';
const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );
}

/** hidden-address listings are drawn as a ~1 km area, never a pin */
const APPROX_RADIUS_M = 1000;

function circle(lng: number, lat: number, meters: number, steps = 48): GeoJSON.Position[] {
  const out: GeoJSON.Position[] = [];
  const dLat = meters / 111_320;
  const dLng = meters / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    out.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return out;
}

export function MapView(p: MapViewProps) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const loaded = useRef(false);
  const originMarker = useRef<maplibregl.Marker | null>(null);
  const cb = useRef(p);
  cb.current = p;

  useEffect(() => {
    if (!el.current) return;
    let m: maplibregl.Map;
    try {
      m = new maplibregl.Map({
        container: el.current,
        style: p.styleUrl,
        center: [p.initial.lng, p.initial.lat],
        zoom: p.initial.z,
        attributionControl: { compact: true },
        dragRotate: false,
        pitchWithRotate: false,
      });
    } catch (err) {
      // No WebGL (old device, blocked GPU, some privacy modes): the constructor throws. Left
      // uncaught it unmounts the whole page, so report it and let the list and search carry on.
      console.error('[map] could not start', err);
      cb.current.onUnavailable?.();
      return;
    }
    m.touchZoomRotate.disableRotation();
    // diagnostics: surface map errors, and let the instance be inspected from devtools
    m.on('error', (e) => console.error('[map]', (e as { error?: Error }).error?.message ?? e));
    (el.current as HTMLDivElement & { __map?: maplibregl.Map }).__map = m;
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

    // 'style.load' fires once the style is parsed; 'load' would wait for every basemap tile.
    m.once('style.load', () => {
      // approximate areas (hidden addresses) under everything else
      m.addSource('approx', { type: 'geojson', data: EMPTY });
      m.addLayer({
        id: 'approx-fill',
        type: 'fill',
        source: 'approx',
        paint: { 'fill-color': BRAND, 'fill-opacity': 0.12 },
      });
      m.addLayer({
        id: 'approx-line',
        type: 'line',
        source: 'approx',
        paint: {
          'line-color': BRAND,
          'line-opacity': 0.55,
          'line-width': 1.5,
          'line-dasharray': [2, 2],
        },
      });

      m.addSource('events', {
        type: 'geojson',
        data: EMPTY,
        cluster: true,
        clusterRadius: 44,
        clusterMaxZoom: 12,
      });
      m.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'events',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': BRAND,
          'circle-opacity': 0.92,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 3,
          'circle-radius': ['step', ['get', 'point_count'], 17, 10, 22, 50, 28],
        },
      });
      m.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'events',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-size': 13,
          'text-font': ['Noto Sans Bold'],
        },
        paint: { 'text-color': '#ffffff' },
      });
      m.addLayer({
        id: 'points',
        type: 'circle',
        source: 'events',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ['case', ['get', 'visited'], '#94a3b8', ['get', 'color']],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.5,
          'circle-radius': 14,
        },
      });
      m.addLayer({
        id: 'point-num',
        type: 'symbol',
        source: 'events',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-field': ['to-string', ['get', 'num']],
          'text-size': 12,
          'text-font': ['Noto Sans Bold'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': '#ffffff' },
      });

      m.addSource('shops', { type: 'geojson', data: EMPTY });
      m.addLayer({
        id: 'shop-points',
        type: 'circle',
        source: 'shops',
        paint: {
          'circle-color': '#b45309',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-radius': 10,
        },
      });
      m.addLayer({
        id: 'shop-num',
        type: 'symbol',
        source: 'shops',
        filter: ['>', ['get', 'num'], 0],
        layout: {
          'text-field': ['to-string', ['get', 'num']],
          'text-size': 11,
          'text-font': ['Noto Sans Bold'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': '#ffffff' },
      });

      m.on('click', 'clusters', (e) => {
        const f = m.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0];
        if (!f) return;
        const id = f.properties?.cluster_id as number;
        (m.getSource('events') as maplibregl.GeoJSONSource)
          .getClusterExpansionZoom(id)
          .then((z) =>
            m.easeTo({
              center: (f.geometry as GeoJSON.Point).coordinates as [number, number],
              zoom: z + 0.5,
            }),
          )
          .catch(() => {});
      });
      const select = (e: maplibregl.MapLayerMouseEvent) => {
        const id = e.features?.[0]?.properties?.public_id as string | undefined;
        if (id) cb.current.onSelect(id);
      };
      m.on('click', 'points', select);
      m.on('click', 'approx-fill', select);
      m.on('click', 'shop-points', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const props = f.properties as Record<string, unknown>;
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        const safeWebsiteUrl = (() => {
          if (!props.website_url) return null;
          try {
            const u = new URL(String(props.website_url));
            return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
          } catch {
            return null;
          }
        })();
        const popup = new maplibregl.Popup({ closeButton: true })
          .setLngLat([lng, lat])
          .setHTML(
            `<strong>${escapeHtml(String(props.name))}</strong><br>` +
              `${escapeHtml(String(props.address_1))}, ${escapeHtml(String(props.city))}, ${escapeHtml(String(props.state))}<br>` +
              (props.phone
                ? `<a href="tel:${escapeHtml(String(props.phone))}">${escapeHtml(String(props.phone))}</a><br>`
                : '') +
              (safeWebsiteUrl
                ? `<a href="${escapeHtml(safeWebsiteUrl)}" target="_blank" rel="noopener">Website</a><br>`
                : '') +
              `<a href="${dirUrl}" target="_blank" rel="noopener">Get directions</a><br>` +
              `<a href="#" data-flag="${escapeHtml(String(props.public_id))}">This is closed / wrong info</a>`,
          )
          .addTo(m);
        const popupEl = popup.getElement()?.querySelector('[data-flag]');
        popupEl?.addEventListener('click', (ev) => {
          ev.preventDefault();
          fetch(`/api/v1/shop-places/${String(props.public_id)}/flag`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: '{}',
          }).catch(() => {});
          (popupEl as HTMLElement).textContent = 'Thanks — reported.';
        });
      });
      for (const layer of ['clusters', 'points', 'approx-fill', 'shop-points']) {
        m.on('mouseenter', layer, () => (m.getCanvas().style.cursor = 'pointer'));
        m.on('mouseleave', layer, () => (m.getCanvas().style.cursor = ''));
      }
      loaded.current = true;
      pushData();
    });

    m.on('moveend', (e) => {
      const c = m.getCenter();
      const b = m.getBounds();
      cb.current.onViewChange({
        lat: c.lat,
        lng: c.lng,
        z: m.getZoom(),
        bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        userInitiated: Boolean((e as { originalEvent?: unknown }).originalEvent),
      });
    });

    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
      loaded.current = false;
    };
    // the map is created once per style; later prop changes flow through the effects below
  }, [p.styleUrl]);

  // Reads the latest props via cb.current: the map's 'load' handler holds the first
  // render's closure, so using `p` here would draw an empty list when results arrive first.
  function pushData() {
    const m = mapRef.current;
    if (!m || !loaded.current) return;
    const { features, numbers } = cb.current;
    const exact: GeoJSON.Feature[] = [];
    const approx: GeoJSON.Feature[] = [];
    for (const f of features) {
      if (!f.geometry) continue;
      const [lng, lat] = f.geometry.coordinates;
      if (f.properties.approximate) {
        approx.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [circle(lng, lat, APPROX_RADIUS_M)] },
          properties: { public_id: f.properties.public_id },
        });
      } else {
        exact.push({
          type: 'Feature',
          geometry: f.geometry,
          properties: {
            public_id: f.properties.public_id,
            num: numbers.get(f.properties.public_id) ?? '',
            visited: visited.has(f.properties.public_id),
            color: pinColor(f.properties.public_id),
          },
        });
      }
    }
    const shopFeats: GeoJSON.Feature[] = (cb.current.shopFeatures ?? []).map((f) => ({
      type: 'Feature',
      geometry: f.geometry,
      properties: { ...f.properties, num: cb.current.shopNumbers.get(f.properties.public_id) ?? 0 },
    }));
    (m.getSource('events') as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: exact,
    });
    (m.getSource('approx') as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: approx,
    });
    (m.getSource('shops') as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: shopFeats,
    });
  }
  const visitedVersion = useVisitedVersion();
  useEffect(pushData, [p.features, p.numbers, p.shopFeatures, p.shopNumbers, visitedVersion]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !p.target) return;
    m.flyTo({
      center: [p.target.lng, p.target.lat],
      zoom: p.target.z,
      duration: 700,
      essential: false,
    });
  }, [p.target]);

  // The container is a sized block on phones (52vh) rather than an overlay
  // pinned to the viewport, so its height changes on rotate and on font-size
  // changes. Keep the GL canvas in step with it.
  useEffect(() => {
    const node = el.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (!p.origin) {
      originMarker.current?.remove();
      originMarker.current = null;
      return;
    }
    if (!originMarker.current) {
      const dot = document.createElement('div');
      dot.setAttribute('aria-label', 'Your starting point');
      dot.style.cssText = `width:16px;height:16px;border-radius:50%;background:${INK};border:3px solid #fff;box-shadow:0 0 0 6px rgba(15,23,42,.15)`;
      originMarker.current = new maplibregl.Marker({ element: dot });
    }
    originMarker.current.setLngLat([p.origin.lng, p.origin.lat]).addTo(m);
  }, [p.origin]);

  return (
    <div
      ref={el}
      className="maplibregl-map"
      role="region"
      aria-label="Map of sales. The list has the same results for keyboard users."
    />
  );
}
